
import * as fb from '@/firebase'
import useJwt from '@/auth/jwt/useJwt'
import { getTimeZones } from '@vvo/tzdb'
import BaseServices from '@/store/services/index'
import { handleBlobError, downloadExcel } from '@/utils/blobService'
import rolesDoc from './rolesDoc'
import { EventBus } from '@/@core/utils/eventBus'

const baseService = new BaseServices()

export default {
  state: {
    globalData: {},
    countries: [],
    organizationsGlobal: [],
    message: {},
    levels: [],
    formattedLevels: {},
    loading: {
      getCountries: true,
      getOrganizationsGlobal: true,
      globalData: true
    },
    session: {},
    modularPermissions: {},
    profilesView: {}    
  },
  getters: {
    getLoading: state => state.loading,
    getSession: state => state.session,
    getGlobalData: state => state.globalData,
    getCountries: state => state.countries,
    getMessage: state => state.message,
    getLevels: state => state.levels,
    getOrganizationsGlobal: state => state.organizationsGlobal,
    getFormattedLevels: state => state.formattedLevels,
    getModularPermissions: state => state.modularPermissions,
    getProfilesView: state => state.profilesView
  },
  mutations: {
    setSession(state, val) {
      state.session = val
    },
    setLevels(state, val) {
      state.levels = val?.level1 || []
    },
    setFormattedLevels(state, val) {
      state.formattedLevels = val
    },
    setOrganizationsGlobal(state, data) {
      state.organizationsGlobal = data.map(el => ({ ...el, text: el.name }))
    },
    setLoading(state, data) {
      const loading = state.loading
      loading[data.name] = data.status
      state.loading = { ...loading }
    },
    setGlobalData(state, { id, data }) {
      state.globalData[id] = data
      const storedData = JSON.parse(localStorage.getItem('globalData') || '{}')
      storedData[id] = data
      localStorage.setItem('globalData', JSON.stringify(storedData))
    },
    setCountries(state, val) {
      state.countries = val
    },
    setMessage(state, val) {
      state.message = val
    },
    setModularPermissions(state, val) {
      state.modularPermissions = val
    },
    setProfilesView(state, val) {
      state.profilesView = val
      if (Object.keys(val).length === 0) {
        localStorage.removeItem('profilesView')
      } else {
        localStorage.setItem('profilesView', JSON.stringify(val))
      }
    }
  },
  actions: {
    async reloadService({ getters, dispatch }, reload) {
      const serviceReload = {
        name: reload,
        queryParams: getters[reload]?.meta?.queryParams || null,
        params: getters[reload]?.meta?.params || null,
        total: getters[reload]?.meta?.total || 0
      }
      await dispatch('fetchService', serviceReload)
    },
    async generateResponse({ commit, dispatch, getters }, { service, response }) {
      const { name, id, queryParams = {}, params = {}, showSuccess = false, hideAlert = false, msg, reload, onSuccess } = service
      if (Object.keys(response).length > 0) {
        if (name.indexOf('get') === 0) {
          let data = null        
          if (Array.isArray(response.data)) {
            data = {
              ...response,
              rows: response.data,
              meta: {
                ...response.meta,
                queryParams,
                params,
                current_page: response.meta?.current_page,
                pages: response.meta?.pages,
                rows_on_page: response.meta?.rows_on_page,
                total_rows: response.meta?.total_rows
              },
              total: response.links?.meta?.data_count || response.pagination?.total_items || response.meta?.total || response.meta?.pagination?.total || null
            }
          } else {
            if (response.meta) {
              response.data.meta = response.meta
            }
            if (!response.data) {
              //Este codigo sirve para esos endpoint que no devuelven data, ahora pueden manejar la respuesta acorde a cada caso en los setters.
              response.data = response
            }
            data = response.data
          }
          const setName = `s${(id || name).substr(1)}`
          commit(setName, data)
          if (showSuccess) {
            const alertType = [
              { type: 'delete', word: 'eliminar-elemento' },
              { type: 'save', word: 'guardar' },
              { type: 'create', word: 'guardar' },
              { type: 'update', word: 'actualizar' }
            ]
            const word = alertType.filter(el => name.indexOf(el.type) === 0)[0]?.word || 'generico'
            commit('setMessage', { text: `msg-exito-${msg || word}`, type: 'success' })
          }

          if (reload) {
            commit('setLoading', { name: reload, status: true })
            await dispatch('reloadService', reload)
          }
        }
        if (onSuccess) onSuccess(response)
      }
    },
    fetchMultipleServices({ commit, state, getters, dispatch }, { services, byName = false, showPackSuccess = false, hidePackAlert = false, packMsg, reload, onSuccess, onError }) {
      // Habilita el "cargando" de todos los elementos asociadas a la llamada y el return
      if (reload) commit('setLoading', { name: reload, status: true })
      if (byName) {
        services.map(service => {
          commit('setLoading', { name: service.id || service.name, status: true })
        })
      }
      // Verifica si es un paquete de llamadas identicas (como el caso de los delete)
      //falta validar que cuando sea byName = true, no haya 2 ids iguales
      const name = services[0].id || services[0].name
      const isPack = services.every(service => (name === (service.id || service.name)))

      baseService.callMultipleServices(services, byName)
        .then(response => {
          if ((byName && !!Object.keys(response)?.length) || (!byName && !!response?.length)) {
            // El listado de responses siempre viene en el mismo orden de la llamada
            services.map((service, index) => {
              const nameService = service.id || service.name
              // Se crea el nombre de las mutations
              const setName = `s${(nameService).substr(1)}`
              const identifier = byName ? nameService : index
              let data = response[identifier].data
              commit('setLoading', { name: (nameService), status: false })

              if (nameService.indexOf('get') === 0) {
                // Cuando el servicio get es de tipo listado, almacena la información relevante a la consulta del mismo como los params, queryParams y el total de registros
                if (Array.isArray(response[identifier]?.data)) {
                  data = {
                    rows: response[identifier].data,
                    meta: {
                      total: response[identifier].links?.meta?.data_count || null,
                      queryParams: service.queryParams,
                      params: service.params
                    }
                  }
                }
                commit(setName, data)
              }
              if (service.onSuccess) service.onSuccess(data)
              // Si el servicio no es de tipo get, normalmente no nos interesa la respuesta más que saber si fue exitosa o no
              // Pero es posible que a futuro esto deba cambiar
            })

            // Se genera alerta de paquete si así se desea
            if (isPack && showPackSuccess) {
              const alertType = [
                { type: 'delete', word: 'eliminar-multiple' },
                { type: 'save', word: 'guardar-multiple' },
                { type: 'update', word: 'actualizar-multiple' },
                { type: 'get', word: 'cargar' }
              ]
              const word = alertType.filter(el => name.indexOf(el.type) === 0)[0]?.word || 'generico-multiple'
              commit('setMessage', { text: `msg-exito-${packMsg || word}`, type: 'success' })
            }
            // Faltaría generar alerta para llamadas que no sean tipo paquete

            // Si al terminar la operación actual, debe recargar alguna información
            if (reload) {
              commit('setLoading', { name: reload, status: true })
              dispatch('reloadService', reload)
            }
            if (onSuccess) onSuccess(response)
          }
        })
        .catch(err => {
          if (reload) commit('setLoading', { name: reload, status: false })
          if (onError) onError(err)
          console.error(err)
          // Automatización de alertas generales en caso de error
          if (isPack || !hidePackAlert) {
            const alertType = [
              { type: 'delete', word: 'eliminar-multiple' },
              { type: 'save', word: 'guardar-multiple' },
              { type: 'update', word: 'actualizar-multiple' },
              { type: 'get', word: 'cargar' }
            ]
            const word = alertType.filter(el => name.indexOf(el.type) === 0)[0]?.word || 'generico-multiple'
            commit('setMessage', { text: `msg-problema-${packMsg || word}`, code: err, type: 'alert' })
          }
        })
    },
    fetchService({ commit, dispatch }, service) {
      if (service.reload) commit('setLoading', { name: service.reload, status: true })
      commit('setLoading', { name: service.id || service.name, status: true })
      // Llamada común y silvestre de servicio
      baseService.callService(service.name, service.queryParams, service.params, null, service.headers)
        .then(response => {
          commit('setLoading', { name: service.id || service.name, status: false })
          // Dispatch genérico para tratar con las respuestas
          dispatch('generateResponse', { service, response })
        })
        .catch(err => {
          if (service.reload) commit('setLoading', { name: service.reload, status: false })
          if (service.onError) service.onError(err)
          console.error(err)
          if (!service.hideAlert) {
            // Automatización de alertas generales en caso de error
            const alertType = [
              { type: 'delete', word: 'eliminar-elemento' },
              { type: 'save', word: 'guardar' },
              { type: 'create', word: 'guardar' },
              { type: 'update', word: 'actualizar' },
              { type: 'get', word: 'cargar' }
            ]
            const word = alertType.filter(el => service.name.indexOf(el.type) === 0)[0]?.word || 'generico'
            commit('setMessage', { text: `msg-problema-${service.msg || word}`, code: err, type: 'alert' })
          }
        })
        .finally(() => {
          commit('setLoading', { name: service.id || service.name, status: false })
        })
    },
    fetchServiceStatusOnError({ commit, dispatch }, service) {
      //Este servicio es para poder tener el status code de los errores en caso que se desee personalizacion las acciones del usuario en base al tipo de error
      baseService.callServiceStatusOnError(service.name, service.queryParams, service.params, {}, true)
        .then(response => {
          dispatch('generateResponse', { service, response })
        })
        .catch(err => {
          service.onError(err)
        })
    },
    fetchCountries({ commit }, data) {
      baseService.callService('getCountries')
        .then(response => {
          commit('setCountries', response.data.map(el => ({ ...el, text: el.name })))
        })
        .catch(err => {
          console.error(err)
          commit('setMessage', { text: 'msg-problema-cargar-datos', code: err, type: 'alert' })
        })
    },
    fetchGlobalData({ commit, state, dispatch }, data) {
      useJwt.setToken(data.user.token)

      const getGlobalOrganizations = () => {        
        commit('setLoading', { name: 'getGlobalData', status: true })
        if (['admin', 'superadmin'].includes(data.user.role)) {
          commit('setLoading', { name: 'getOrganizationsGlobal', status: true })
          baseService.callMultipleServices([{ name: 'getOrganizationsGlobal', queryParams: { simplified: true } }], true)
            .then(response => {
              commit('setLoading', { name: 'getOrganizationsGlobal', status: false })
              if (response.getOrganizationsGlobal?.data) {
                commit('setOrganizationsGlobal', response.getOrganizationsGlobal.data)
              }
            })
            .catch(err => {
              commit('setLoading', { name: 'getGlobalData', status: false })
              console.error(err)
            })
        } else {
          commit('setLoading', { name: 'getOrganizationsGlobal', status: false })
          commit('setLoading', { name: 'getGlobalData', status: false })
        }
      }

      const setUserCountriesConfig = (countries) => {
        commit('setCountries', countries)
        data.user.country = countries.filter(country => {
          const myCountry = data.user.shipper?.country_id || data.user.shipper?.country?.id ||
            data.user.organization?.country?.id || data.user.organization?.country_id ||
            data.user.country?.id || data.user.country_id || 1
          return country.id === myCountry
        })[0]
        data.user.country._id = data.user.country.id
        if (!data.user.timezone) {
          data.user.timezone = getTimeZones().filter(tz => {
            return tz.currentTimeOffsetInMinutes === data.user.country.main_time_zone * 60 && tz.countryCode === data.user.country.code
          })[0]?.name || 'America/Santiago'
        }
        commit('setGlobalData', { id: 'countries', data: countries })
        commit('setIniUser', data.user)
        commit('setLoading', { name: 'getGlobalData', status: false })
        setTimeout(() => {
          document.getElementById('loading-bg').style.display = 'none'
          document.getElementById('loading-text').innerHTML = ''
        }, 500)
        if (['app-render', 'custom-token'].includes(data.from)) EventBus.$emit('reload-menu', data.user.role, data.user?.legacy_data?.profile, data.user?.profile)
        else if (data.onSuccess) data.onSuccess()
      }    
      // Recuperar datos de localStorage si existen
      const storedGlobalData = JSON.parse(localStorage.getItem('globalData')) || {}
      getGlobalOrganizations()
    
      // Manejar carga de datos para `getCountries`
      if (Object.entries(storedGlobalData).length) {        
        const storedCountries = storedGlobalData.countries || null
        setUserCountriesConfig(storedCountries)
      } else {
        const services = [
          { name: 'getCountries' }
        ]
        commit('setLoading', { name: 'getCountries', status: true })
        //getGlobalOrganizations()
        baseService.callMultipleServices(services, true)
          .then(response => {
            commit('setLoading', { name: 'getCountries', status: false })
            const countries = response.getCountries.data.map(el => ({ ...el, text: el.name }))  
            setUserCountriesConfig(countries)
          })
          .catch(err => {
            commit('setLoading', { name: 'getGlobalData', status: false })
            console.error(err)
          })
      }
    },
    async fetchPermissions({ commit, dispatch }, data) {
      commit('setLoading', { name: 'getGlobalData', status: true })
    
      const loadingElement = document.getElementById('loading-text')
      if (loadingElement) loadingElement.innerHTML = 'Configurando su cuenta'
    
      try {
        // Fix solo para walmart
        const profiles_walmart = {
          3: 'contact_center'
        }
        const isProfile = Boolean(data.user.profile)
        const profileName = data.user.profile

        const roleName = data.user.role

        const collectionKey = isProfile ? 'profilesCollection' : 'rolesCollection'

        const userTypeKey = isProfile ? profileName : profiles_walmart[data.user.profile_type] || rolesDoc[roleName]

        const userTypeDocRef = fb[collectionKey].doc(userTypeKey)

        const permissionCol = (await fb.resourcesPermissionsCollection.doc('default').get()).data()
        await dispatch('handleUserType', { userTypeDocRef, permissionCol, data, isProfile })
      } catch (error) {
        console.error('Error fetching permissions:', error)
      } finally {
        commit('setLoading', { name: 'getGlobalData', status: false })
      }
    }, 
    async handleUserType({ dispatch }, { userTypeDocRef, permissionCol, data, isProfile }) {
      const userPermissionsData = (await userTypeDocRef.get()).data()
      await dispatch('updatePermissions', { userPermissionsData, permissionCol, data, isProfile })
    },
    async updatePermissions({ commit, dispatch }, { userPermissionsData, permissionCol, data, isProfile }) {
      const menuAccess = await dispatch('setMenuAccessList', {
        userPermissionsData: userPermissionsData.permission_views,
        user: data.user
      })
  
      useJwt.setToken(data.user.token, 'global state')
      data.user.ability = useJwt.generateAbility(menuAccess, permissionCol)
  
      if (isProfile) {
        commit('setProfilesView', userPermissionsData)
        window.dispatchEvent(new Event('permissions-updated'))
      }
      
      await dispatch('fetchGlobalData', data)
    },
    setMenuAccessList({ commit }, { userPermissionsData, user }) {
      let menuAccess = null
      // Si el usuario tiene profile, solo se usan los permisos del profile
      if (user.profile) {
        menuAccess = userPermissionsData
      } else {
        // Combina permission_views y restriction_views en un solo paso
        menuAccess = userPermissionsData.concat(user.permission_views || [])
        if (user.restriction_views?.length) {
          menuAccess = menuAccess.filter(item => !user.restriction_views.includes(item))
        }
      }

      // Genera el nuevo objeto de permisos de manera más concisa
      const newMenuPermission = menuAccess.reduce((permissions, key) => {
        permissions[key] = [{ entities: ['*'], methods: ['*'] }]
        return permissions
      }, {})
    
      // Agrega permisos personalizados del usuario
      Object.keys(user.permissions).forEach(key => {
        if (!key.startsWith('menu.')) {
          newMenuPermission[key] = user.permissions[key]
        }
      })

      return newMenuPermission
    },
    
    fetchSession({ commit }, data) {
      commit('setSession', data)
    },
    
    fetchServiceGenerateExcel({ commit, dispatch }, service) {
      const { onSuccess, onError, name, queryParams, params, headers, fileName, showToast, responseType } = service
      commit('setLoading', { name, status: true })
      baseService.callService(name, queryParams, params, { fullResponse: true, fullResponseError: true }, headers, responseType)
        .then((res) => {
          downloadExcel(res.data, fileName)
          if (service.onSuccess) onSuccess(res)
        })
        .catch(err => {
          handleBlobError(err, showToast)
          if (service.onError) onError(err)
        })
        .finally(() => {
          commit('setLoading', { name, status: false })
        })
    },

    fetchServiceGenerateExcelPudos({ commit, dispatch }, service) {
      const { onSuccess, onError, name, queryParams, params, headers, fileName, showToast, responseType } = service
      baseService.callService(name, queryParams, params, { fullResponse: true, fullResponseError: true }, headers, responseType)
        .then((res) => {
          const contentType = res.headers['content-type']
          if (contentType.includes('text') || contentType.includes('application/json')) {
            // Si devuelve un texto o un json no se descarga en excel sino que se manda el texto para mostrar una alerta
            res.data.text().then((text) => {
              if (service.onSuccess) onSuccess(res, text)
            })
          } else {
            downloadExcel(res.data, fileName)
            if (service.onSuccess) onSuccess(res)
          }
        })
        .catch(err => {
          handleBlobError(err, showToast)
          if (service.onError) onError(err)
        })
    }
  }
}
